import React, { useMemo } from 'react';
import { useDevicePreferredImageFormat } from '@app/ui';
import { Image } from '@stockx/ui';
import { isServer } from '@app/utils';
import {
  PLACEHOLDER_IMAGE_URL,
  VALID_SRC_DOMAINS,
  BASE_VARIABLE_QUALITIES,
  CATALOG_IMAGE_BASE_HEIGHT,
  CATALOG_IMAGE_BASE_WIDTH,
} from '../../consts';
import buildImageSrc from '../../utils/buildImageSrc';

interface CatalogImageProps {
  src: string;
  height?: number;
  width?: number;
  /**
   * The native HTML `width` attribute to the passed to the `img`
   */
  htmlWidth?: string | number;
  /**
   * The native HTML `height` attribute to the passed to the `img`
   */
  htmlHeight?: string | number;
  extraImageParams?: object;
  isPriority?: boolean;
  isIncreasedSize?: boolean;

  [x: string]: any;
}

/**
 * Catalog images can typically be used for any product image.
 *
 * Safari has multiple image related problems, including:
 * -- Multiple images are downloaded when using src + srcset
 * -- Old versions of safari do not support webp
 *
 * @param src
 * @param width
 * @param height
 * @param htmlHeight
 * @param htmlWidth
 * @param isPriority
 * @param isIncreasedSize
 * @param extraImageParams
 * @param restProps
 * @constructor
 */
const CatalogImage: React.FC<CatalogImageProps> = ({
  src,
  width = CATALOG_IMAGE_BASE_WIDTH,
  height = CATALOG_IMAGE_BASE_HEIGHT,
  htmlHeight,
  htmlWidth,
  isPriority = false,
  extraImageParams = {},
  is360 = false,
  ...restProps
}) => {
  const preferredImageFormat = useDevicePreferredImageFormat();
  const isPlaceholderImage = src?.includes('Placeholder');
  const isValidDomain = VALID_SRC_DOMAINS.some((domain) =>
    src?.includes(domain),
  );

  const usePlaceholderImage = isPlaceholderImage || !isValidDomain || !src;

  const { fallbackSrc, fallbackSrcSet } = useMemo(() => {
    const { imageSrc: fallbackSrc, imageSrcSet: fallbackSrcSet } =
      buildImageSrc({
        src: PLACEHOLDER_IMAGE_URL,
        dpr: 2,
        // If SSR on mobile, render best-guess dimensions of 360x240
        width,
        height,
        preferredImageFormat,
        variableQualities: BASE_VARIABLE_QUALITIES,
      });

    return {
      fallbackSrc,
      fallbackSrcSet,
    };
  }, [height, preferredImageFormat, width]);

  //
  // Generate Image Src
  const srcProps = useMemo(() => {
    //
    // if we are using placeholder image we do not need to generate this.
    if (usePlaceholderImage) {
      return {};
    }

    const { imageSrc, imageSrcSet } = buildImageSrc({
      src,
      extraImageParams,
      // If SSR on mobile, render best-guess dimensions of 360x240
      width,
      height,
      preferredImageFormat,
      variableQualities: BASE_VARIABLE_QUALITIES,
    });

    return {
      srcSet: imageSrcSet,
      src: imageSrc,
    };
  }, [
    usePlaceholderImage,
    preferredImageFormat,
    extraImageParams,
    height,
    src,
    width,
    isValidDomain,
  ]);

  if (usePlaceholderImage) {
    return (
      <Image
        referrerPolicy="origin"
        backgroundImage={`url('${fallbackSrc}')`}
        htmlWidth={htmlWidth}
        htmlHeight={htmlHeight}
        srcSet={fallbackSrcSet}
        src={fallbackSrc}
        {...restProps}
      />
    );
  }

  return (
    <Image
      referrerPolicy="origin"
      htmlWidth={htmlWidth}
      htmlHeight={htmlHeight}
      ignoreFallback={srcProps?.src || isServer || is360}
      fallbackSrc={fallbackSrc}
      fetchpriority={isPriority ? 'high' : undefined}
      {...srcProps}
      {...restProps}
    />
  );
};

export default CatalogImage;
